import {
  AttachFile as AttachFileIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ContractorRegistrationApiService from "../../../infrastructure/api/contractorRegistration";

const statusColors = {
  1: { color: "warning", text: "در انتظار بررسی" }, // Pending
  2: { color: "info", text: "در حال بررسی" }, // UnderReview
  3: { color: "success", text: "تایید شده" }, // Approved
  4: { color: "error", text: "رد شده" }, // Rejected
  5: { color: "secondary", text: "نیاز به اطلاعات تکمیلی" }, // NeedsMoreInfo
};

const ContractorRegistrationManagement = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState({
    status: "",
    reviewNotes: "",
  });
  const [statistics, setStatistics] = useState({});
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    loadRegistrations();
    loadStatistics();
  }, [filterStatus]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const response = await ContractorRegistrationApiService.getRegistrations(
        filterStatus || null
      );

      if (response.isSuccess) {
        setRegistrations(response.data);
      } else {
        toast.error("خطا در دریافت لیست درخواست‌ها");
      }
    } catch (error) {
      console.error("Error loading registrations:", error);
      toast.error("خطا در دریافت لیست درخواست‌ها");
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await ContractorRegistrationApiService.getStatistics();
      if (response.isSuccess) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error("Error loading statistics:", error);
    }
  };

  const handleViewDetails = async (registration) => {
    try {
      const response =
        await ContractorRegistrationApiService.getRegistrationById(
          registration.id
        );
      if (response.isSuccess) {
        setSelectedRegistration(response.data);
        setDetailsOpen(true);
      } else {
        toast.error("خطا در دریافت جزئیات درخواست");
      }
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("خطا در دریافت جزئیات درخواست");
    }
  };

  const handleReview = (registration) => {
    setSelectedRegistration(registration);
    setReviewData({
      status: registration.status,
      reviewNotes: registration.reviewNotes || "",
    });
    setReviewOpen(true);
  };

  const handleSubmitReview = async () => {
    try {
      const response =
        await ContractorRegistrationApiService.updateRegistrationStatus({
          registrationId: selectedRegistration.id,
          status: parseInt(reviewData.status),
          reviewNotes: reviewData.reviewNotes,
        });

      if (response.isSuccess) {
        toast.success("وضعیت درخواست به‌روزرسانی شد");
        setReviewOpen(false);
        loadRegistrations();
        loadStatistics();
      } else {
        toast.error(response.message || "خطا در به‌روزرسانی وضعیت");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("خطا در به‌روزرسانی وضعیت");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fa-IR");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        مدیریت درخواست‌های ثبت‌نام پیمانکاران
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                کل درخواست‌ها
              </Typography>
              <Typography variant="h4">{statistics.totalCount || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                در انتظار بررسی
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statistics.pendingCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                در حال بررسی
              </Typography>
              <Typography variant="h4" color="info.main">
                {statistics.underReviewCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                تایید شده
              </Typography>
              <Typography variant="h4" color="success.main">
                {statistics.approvedCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                رد شده
              </Typography>
              <Typography variant="h4" color="error.main">
                {statistics.rejectedCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>فیلتر بر اساس وضعیت</InputLabel>
              <Select
                value={filterStatus}
                label="فیلتر بر اساس وضعیت"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="1">در انتظار بررسی</MenuItem>
                <MenuItem value="2">در حال بررسی</MenuItem>
                <MenuItem value="3">تایید شده</MenuItem>
                <MenuItem value="4">رد شده</MenuItem>
                <MenuItem value="5">نیاز به اطلاعات تکمیلی</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={() => setFilterStatus("")}>
              پاک کردن فیلتر
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>نام و نام خانوادگی</TableCell>
              <TableCell>شرکت</TableCell>
              <TableCell>موبایل</TableCell>
              <TableCell>ایمیل</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>تاریخ ثبت</TableCell>
              <TableCell>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {registrations
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((registration) => (
                <TableRow key={registration.id}>
                  <TableCell>
                    {registration.firstName} {registration.lastName}
                  </TableCell>
                  <TableCell>{registration.companyName}</TableCell>
                  <TableCell>{registration.mobile}</TableCell>
                  <TableCell>{registration.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusColors[registration.status]?.text}
                      color={statusColors[registration.status]?.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(registration.createOn)}</TableCell>
                  <TableCell>
                    <Tooltip title="مشاهده جزئیات">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(registration)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="بررسی درخواست">
                      <IconButton
                        size="small"
                        onClick={() => handleReview(registration)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={registrations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="تعداد ردیف در صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} از ${count !== -1 ? count : `بیش از ${to}`}`
          }
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>جزئیات درخواست ثبت‌نام پیمانکار</DialogTitle>
        <DialogContent>
          {selectedRegistration && (
            <Box>
              {/* Personal Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                اطلاعات شخصی
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>
                    <strong>نام:</strong> {selectedRegistration.firstName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>نام خانوادگی:</strong>{" "}
                    {selectedRegistration.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>کد ملی:</strong> {selectedRegistration.nationalCode}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>تاریخ تولد:</strong>{" "}
                    {formatDate(selectedRegistration.birthDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>موبایل:</strong> {selectedRegistration.mobile}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>ایمیل:</strong> {selectedRegistration.email}
                  </Typography>
                </Grid>
              </Grid>

              {/* Company Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                <BusinessIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                اطلاعات شرکت
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography>
                    <strong>نام شرکت:</strong>{" "}
                    {selectedRegistration.companyName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>شماره ثبت:</strong>{" "}
                    {selectedRegistration.companyRegistrationNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>تلفن شرکت:</strong>{" "}
                    {selectedRegistration.companyPhone || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <strong>آدرس:</strong> {selectedRegistration.companyAddress}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>استان:</strong> {selectedRegistration.province}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    <strong>شهر:</strong> {selectedRegistration.city}
                  </Typography>
                </Grid>
              </Grid>

              {/* Documents */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                <AttachFileIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                مدارک
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                <ListItem>
                  <ListItemText
                    primary="کارت ملی"
                    secondary={
                      <Button
                        size="small"
                        href={selectedRegistration.nationalCardImageUrl}
                        target="_blank"
                      >
                        مشاهده فایل
                      </Button>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="مجوز شرکت"
                    secondary={
                      <Button
                        size="small"
                        href={selectedRegistration.companyLicenseImageUrl}
                        target="_blank"
                      >
                        مشاهده فایل
                      </Button>
                    }
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>بررسی درخواست</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>وضعیت جدید</InputLabel>
              <Select
                value={reviewData.status}
                label="وضعیت جدید"
                onChange={(e) =>
                  setReviewData((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <MenuItem value="2">در حال بررسی</MenuItem>
                <MenuItem value="3">تایید شده</MenuItem>
                <MenuItem value="4">رد شده</MenuItem>
                <MenuItem value="5">نیاز به اطلاعات تکمیلی</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="یادداشت بررسی"
              value={reviewData.reviewNotes}
              onChange={(e) =>
                setReviewData((prev) => ({
                  ...prev,
                  reviewNotes: e.target.value,
                }))
              }
              helperText="توضیحات مربوط به تصمیم گرفته شده"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>انصراف</Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={!reviewData.status}
          >
            ثبت بررسی
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorRegistrationManagement;
